import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getShareByToken, incrementShareDownload, incrementShareView, type ShareRow } from '@/lib/zcall';
import { md5 } from '@/lib/md5';
import DeckViewer from '@/components/feature/presentation/DeckViewer';
import type { SlideDeck } from '@/lib/slideDeck';

const BRAND_LOGO = 'https://storage.helloreaddy.io/project_files/59392c9e-e303-496e-bac1-59ba5941cf65/07452721-5131-4b23-922e-acc73d68a0a9_compressed_zifek.webp';

/**
 * Page publique d'un partage ZCall : consultable par n'importe qui possédant le
 * lien, avec ou sans compte. Mot de passe facultatif.
 */
export default function PartagePage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState<ShareRow | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [wrong, setWrong] = useState(false);
  const [active, setActive] = useState(0);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    (async () => {
      const row = await getShareByToken(token);
      setShare(row);
      if (!row || !row.password_hash) setUnlocked(true);
      setLoading(false);
    })();
  }, [token]);

  const submitPassword = () => {
    if (!share?.password_hash) { setUnlocked(true); return; }
    if (md5(password.trim()) === share.password_hash) { setUnlocked(true); setWrong(false); }
    else setWrong(true);
  };

  // Compteur de consultations : une seule fois par ouverture du lien.
  useEffect(() => {
    if (!unlocked || !share || viewedRef.current) return;
    viewedRef.current = true;
    void incrementShareView(share.id);
  }, [unlocked, share]);

  const items = share?.content || [];
  const current = items[active];

  const renderDoc = () => {
    if (!current) return null;
    if (current.type === 'presentation' && current.deck) {
      return <DeckViewer deck={current.deck as SlideDeck} title={current.name} />;
    }
    if (current.type === 'pdf' && current.pdfBase64) {
      return (
        <iframe
          title={current.name}
          src={`data:application/pdf;base64,${current.pdfBase64}`}
          className="w-full h-[70vh] rounded-lg border border-background-200/70"
        />
      );
    }
    return (
      <div
        className="prose max-w-none text-sm text-foreground-800 leading-relaxed overflow-x-auto"
        dangerouslySetInnerHTML={{ __html: current.html || '<p class="text-foreground-400">Aperçu non disponible.</p>' }}
      />
    );
  };

  /** Télécharge le document courant (PDF en PDF, sinon en fichier HTML autonome). */
  const downloadCurrent = () => {
    if (!current) return;
    const safe = (current.name || 'document').replace(/[^a-zA-Z0-9._ -]/g, '_').slice(0, 80) || 'document';
    let blob: Blob;
    let ext: string;
    if (current.type === 'pdf' && current.pdfBase64) {
      const binary = atob(current.pdfBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      blob = new Blob([bytes], { type: 'application/pdf' });
      ext = 'pdf';
    } else {
      const html = `<meta charset="utf-8"><title>${safe}</title>${current.html || ''}`;
      blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      ext = 'html';
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `${safe}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    if (share) void incrementShareDownload(share.id);
  };

  const downloadAll = () => {
    items.forEach((_, i) => {
      setTimeout(() => {
        setActive(i);
        const it = items[i];
        if (!it) return;
        // Rendu direct du document ciblé (indépendant de l'état `active`).
        const safe = (it.name || 'document').replace(/[^a-zA-Z0-9._ -]/g, '_').slice(0, 80) || 'document';
        let blob: Blob;
        let ext: string;
        if (it.type === 'pdf' && it.pdfBase64) {
          const binary = atob(it.pdfBase64);
          const bytes = new Uint8Array(binary.length);
          for (let j = 0; j < binary.length; j += 1) bytes[j] = binary.charCodeAt(j);
          blob = new Blob([bytes], { type: 'application/pdf' });
          ext = 'pdf';
        } else {
          blob = new Blob([`<meta charset="utf-8"><title>${safe}</title>${it.html || ''}`], { type: 'text/html;charset=utf-8' });
          ext = 'html';
        }
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `${safe}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
      }, i * 400);
    });
    if (share) void incrementShareDownload(share.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  if (!share) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-link-unlink-m text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-lg font-semibold text-foreground-900 mb-2">Partage introuvable</h1>
          <p className="text-sm text-foreground-500">Ce lien est invalide ou a été supprimé.</p>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 p-4">
        <div className="w-full max-w-sm bg-background-50 border border-background-200/70 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-accent-50 flex items-center justify-center">
            <i className="ri-lock-line text-2xl text-accent-600"></i>
          </div>
          <h1 className="text-base font-semibold text-foreground-900 mb-1">{share.title || 'Partage protégé'}</h1>
          <p className="text-xs text-foreground-500 mb-5">Ce contenu est protégé par un mot de passe.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitPassword(); }}
            placeholder="Mot de passe"
            className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300 mb-3"
          />
          {wrong && <p className="text-xs text-red-500 mb-3">Mot de passe incorrect.</p>}
          <button
            onClick={submitPassword}
            className="w-full py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-accent-600 whitespace-nowrap"
          >
            Accéder au contenu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <header className="flex items-center gap-2.5 px-4 md:px-6 py-4 border-b border-background-200/70">
        <span className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
          <img src={BRAND_LOGO} alt="ZIFEK" title="ZIFEK" className="w-full h-full object-cover" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 truncate">{share.title || 'Documents partagés'}</p>
          <p className="text-[11px] text-foreground-500">{items.length} document{items.length !== 1 ? 's' : ''}</p>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row gap-6 p-4 md:p-6 max-w-6xl mx-auto">
        <aside className="w-full lg:w-64 flex-shrink-0">
          <div className="space-y-1">
            {items.map((it, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left cursor-pointer transition-colors ${
                  active === i ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className={it.type === 'excel' ? 'ri-file-excel-2-line text-green-600' : it.type === 'pdf' ? 'ri-file-pdf-2-line text-red-600' : it.type === 'powerpoint' || it.type === 'presentation' ? 'ri-file-ppt-2-line text-orange-500' : 'ri-file-word-2-line text-teal-600'}></i>
                <span className="text-sm truncate">{it.name}</span>
              </button>
            ))}
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h2 className="text-base font-semibold text-foreground-900 min-w-0 break-words">{current?.name}</h2>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={downloadCurrent}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-download-2-line"></i>Télécharger
                </button>
                {items.length > 1 && (
                  <button
                    onClick={downloadAll}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors"
                    title="Télécharger tous les documents"
                  >
                    <i className="ri-download-cloud-2-line"></i>Tout
                  </button>
                )}
              </div>
            </div>
            {renderDoc()}
          </div>
        </main>
      </div>
    </div>
  );
}